import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import * as I from 'immutable';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import AppNav from 'app/components/AppNav';
import {NewPortfolioLayersSelector} from 'app/components/Library/PortfolioLayersSelector';
import {usePushNotification} from 'app/components/Notification';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import {createLayerObjectFromLayerKeys} from 'app/utils/layerUtils';
import * as routeUtils from 'app/utils/routeUtils';

import {AddPropertiesOptions} from './AddPropertiesOptions';
import {useManageFeatures} from './ManageFeaturesProvider';
import {ManagePropertiesWrapper} from './ManagePropertiesWrapper/ManagePropertiesWrapper';
import cs from './styles.module.scss';

export interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  loggedInUserActions: LoggedInUserActions;
  projectId: string;
}

const ManageFeaturesSelectMode: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  organization,
  profile,
  loggedInUserActions,
  projectId,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const orgIdPrefix = routeUtils.makeUuidPrefix(organization.get('id'));
  const cameFromPortfolioView = location.pathname.split('/').includes('select');
  const [isSelectLayersModalOpen, setIsSelectLayersModalOpen] = React.useState(false);
  const [newPortfolioName, setNewPortfolioName] = React.useState('');
  const [isCreatingPortfolio, setIsCreatingPortfolio] = React.useState(false);
  const pushNotification = usePushNotification();
  const [, , projectsActions] = useProjects();
  const {
    manageFeaturesState: {selectedLayerKeys, selectedOverlayIds},
    manageFeaturesActions: {setSelectedLayerKeys, setSelectedOverlayIds},
  } = useManageFeatures();

  React.useEffect(() => {
    if (isSelectLayersModalOpen) {
      setNewPortfolioName('');
    }
  }, [isSelectLayersModalOpen]);

  const handleConfirmNewPortfolio = async () => {
    const portfolioName = newPortfolioName.trim();
    setIsCreatingPortfolio(true);
    try {
      const result = await projectsActions.createProject(
        organization as I.MapAsRecord<I.ImmutableFields<ApiOrganization>>,
        portfolioName,
        createLayerObjectFromLayerKeys(selectedLayerKeys),
        selectedOverlayIds
      );
      if (!result) {
        pushNotification({
          message: "We weren't able to create the portfolio right now. Please try again.",
          options: {intent: B.Intent.DANGER},
        });
        Sentry.captureException(
          new Error(
            `createProject for a new empty portfolio returned undefined in ManageFeaturesSelectMode for ${organization.get('id')}`
          )
        );
        return;
      }
      setIsSelectLayersModalOpen(false);
      navigate(`/${orgIdPrefix}/projects/${routeUtils.makeUuidPrefix(result.projectId)}`);
    } catch (e) {
      pushNotification({
        message: "We weren't able to create the portfolio right now. Please try again.",
        options: {intent: B.Intent.DANGER},
      });
      Sentry.captureException(e);
    } finally {
      setIsCreatingPortfolio(false);
    }
  };

  return (
    <div className={cs.selectModePageRoot}>
      <AppNav
        organization={organization}
        profile={profile}
        selectedProject={null}
        loggedInUserActions={loggedInUserActions}
      />
      <ManagePropertiesWrapper>
        <div className={cs.selectModeTitleBlock}>
          <h2>Add new properties</h2>
        </div>
        <AddPropertiesOptions organization={organization} projectId={projectId} />
        {projectId && (
          <div className={cs.selectModeSection}>
            <h2>Update existing properties</h2>
            <div className={cs.selectModeOptions}>
              <B.Button
                minimal
                onClick={() => navigate(`/${orgIdPrefix}/admin/updateProperties/${projectId}`)}
              >
                <div className={cs.selectModeOption}>
                  <B.Icon icon="folder-shared" size={32} />
                  <h4>Upload from a file</h4>
                </div>
              </B.Button>
            </div>
          </div>
        )}
        <div className={cs.goBack}>
          <>
            <a onClick={() => setIsSelectLayersModalOpen(true)}>skip adding properties for now </a>
          </>
          or{' '}
          <a onClick={() => navigate(-1)}>
            go back to {cameFromPortfolioView ? 'your portfolio' : 'your homepage'}
          </a>
        </div>
      </ManagePropertiesWrapper>
      <NewPortfolioLayersSelector
        isOpen={isSelectLayersModalOpen}
        onClose={() => setIsSelectLayersModalOpen(false)}
        selectedLayerKeys={selectedLayerKeys}
        setSelectedLayerKeys={setSelectedLayerKeys}
        selectedOverlayIds={selectedOverlayIds}
        setSelectedOverlayIds={setSelectedOverlayIds}
        dialogFooter={
          <B.DialogFooter
            actions={
              <B.Button
                intent={B.Intent.PRIMARY}
                loading={isCreatingPortfolio}
                disabled={isCreatingPortfolio || !newPortfolioName.trim()}
                onClick={handleConfirmNewPortfolio}
                text="Confirm"
                large
              />
            }
          >
            <B.InputGroup
              id="skip-new-portfolio-name"
              className={`${cs.nameInput} ${cs.newPortfolioNameInput}`}
              value={newPortfolioName}
              onValueChange={setNewPortfolioName}
              placeholder="Name your new portfolio..."
              disabled={isCreatingPortfolio}
              large
            />
          </B.DialogFooter>
        }
      />
    </div>
  );
};

export default ManageFeaturesSelectMode;
